import { PlusIcon } from "@/components/icons";
import { cn } from "@/utils/cn";
import { linkEntity } from "../drawer/taskLinks";
import { TaskCapabilities } from "../taskCapabilities";
import { taskListSections } from "../taskList";
import type { Task, TaskList, TaskViaItem } from "../types";
import { TaskRow } from "./TaskRow";

interface TaskListEditorProps {
  list: TaskList;
  capabilities: TaskCapabilities;
  onToggle: (task: Task) => void;
  /** Opens the caller's task drawer. */
  onOpen: (task: Task) => void;
  /** The muted "+ Add task" row, for a list that is a section of a larger
   * panel. A list that is the panel body uses a PanelHeaderAction (DD-017). */
  onAdd?: () => void;
  /** Follows a via group's header to its item. Omitted in the creator portal. */
  onOpenItem?: (item: TaskViaItem) => void;
  emptyHint?: string;
}

/**
 * A task list (DD-017, DD-052): the own tasks, then one via group per
 * related item under a muted header with the item's icon and title. Via
 * groups are not reorderable.
 */
export function TaskListEditor({
  list,
  capabilities,
  onToggle,
  onOpen,
  onAdd,
  onOpenItem,
  emptyHint = "No tasks yet",
}: TaskListEditorProps) {
  const sections = taskListSections(list);
  const canAdd = capabilities.canCreate && !!onAdd;

  return (
    <div>
      {sections.length > 0 ? (
        <div className="divide-y divide-gray-100">
          {sections.map((section) => (
            <div key={section.key} className={section.item ? "py-2" : undefined}>
              {section.item && <ViaHeader item={section.item} onOpenItem={onOpenItem} />}
              <div className={cn("divide-y", section.item ? "divide-gray-50" : "divide-gray-100")}>
                {section.tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    capabilities={capabilities}
                    onToggle={onToggle}
                    onOpen={onOpen}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-3 text-sm text-gray-400">{emptyHint}</div>
      )}
      {canAdd && (
        <div className="pt-3 mt-1 border-t border-gray-100">
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700"
          >
            <PlusIcon className="w-4 h-4" />
            Add task
          </button>
        </div>
      )}
    </div>
  );
}

function ViaHeader({
  item,
  onOpenItem,
}: {
  item: TaskViaItem;
  onOpenItem?: (item: TaskViaItem) => void;
}) {
  const Icon = linkEntity(item.type).icon;
  const label = (
    <>
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{item.title}</span>
    </>
  );
  return onOpenItem ? (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      className="flex max-w-full items-center gap-1.5 pt-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
    >
      {label}
    </button>
  ) : (
    <div className="flex items-center gap-1.5 pt-1 text-xs font-semibold text-gray-500">{label}</div>
  );
}
